import { HeatTraceAction, HeatTraceActionTypes } from '@completion/actions';
import {
  HeatTraceCircuit,
  HeatTraceInsulationResponse,
  HeatTraceOverview,
  HeatTraceOverviewDisciplineStatus,
  HeatTraceTests,
} from '@completion/models';

const REQUIRED_DISCIPLINE_NUMBERS = ['T', 'C', 'H', 'B', 'X', 'I', 'Z', 'ZB', '2', '3', 'M'] as const;
const REQUIRED_DISCIPLINE_NUMBER_SET = new Set<string>(REQUIRED_DISCIPLINE_NUMBERS);

function normalizeOverview(overview: HeatTraceOverview): HeatTraceOverview {
  const disciplineByNumber = new Map(
    (overview.disciplineStatuses ?? []).map((disciplineStatus) => [disciplineStatus.disciplineNumber, disciplineStatus]),
  );

  const requiredDisciplineStatuses: HeatTraceOverviewDisciplineStatus[] = REQUIRED_DISCIPLINE_NUMBERS.map((disciplineNumber) => {
    const existing = disciplineByNumber.get(disciplineNumber);
    if (existing) {
      return existing;
    }

    return {
      disciplineNumber,
      status: null,
    };
  });

  const extraDisciplineStatuses = (overview.disciplineStatuses ?? []).filter(
    (disciplineStatus) => !REQUIRED_DISCIPLINE_NUMBER_SET.has(disciplineStatus.disciplineNumber),
  );

  return {
    ...overview,
    disciplineStatuses: [...requiredDisciplineStatuses, ...extraDisciplineStatuses],
  };
}

export const initialState: HeatTraceState = {
  testsLoading: false,
  circuits: null,
  circuitsLoading: false,
  currentTagNumber: null,
  insulation: null,
  insulationLoading: false,
  currentInsulationTagNumber: null,
  overview: null,
  overviewLoading: false,
  currentOverviewTagNumber: null,
  pipeTests: [],
  loadErrorMessage: null,
};

export interface HeatTraceState {
  testsLoading: boolean;
  circuits: HeatTraceCircuit | null;
  circuitsLoading: boolean;
  currentTagNumber: string | null;
  insulation: HeatTraceInsulationResponse | null;
  insulationLoading: boolean;
  currentInsulationTagNumber: string | null;
  overview: HeatTraceOverview | null;
  overviewLoading: boolean;
  currentOverviewTagNumber: string | null;
  pipeTests: HeatTraceTests[];
  loadErrorMessage: string | null;
}

export function heatTraceReducer(state: HeatTraceState = initialState, action: HeatTraceAction): HeatTraceState {
  switch (action.type) {
    case HeatTraceActionTypes.HeatTraceLoad:
      return {
        ...state,
        testsLoading: true,
        loadErrorMessage: null,
      };
    case HeatTraceActionTypes.HeatTraceSearch:
      return {
        ...state,
        testsLoading: true,
        pipeTests: [],
        loadErrorMessage: null,
      };
    case HeatTraceActionTypes.HeatTraceLoadSuccess:
      return {
        ...state,
        testsLoading: false,
        pipeTests: action.pipeTests,
        loadErrorMessage: null,
      };
    case HeatTraceActionTypes.HeatTraceLoadFailed:
      return {
        ...state,
        testsLoading: false,
        pipeTests: [],
        loadErrorMessage: action.message,
      };
    case HeatTraceActionTypes.HeatTraceLoadCircuits:
      return {
        ...state,
        circuits: null,
        circuitsLoading: true,
        currentTagNumber: action.tagNumber,
      };
    case HeatTraceActionTypes.HeatTraceLoadCircuitsSuccess:
      return {
        ...state,
        circuits: action.circuits,
        circuitsLoading: false,
      };
    case HeatTraceActionTypes.HeatTraceLoadInsulation:
      return {
        ...state,
        insulation: null,
        insulationLoading: true,
        currentInsulationTagNumber: action.tagNumber,
      };
    case HeatTraceActionTypes.HeatTraceLoadInsulationSuccess:
      return {
        ...state,
        insulation: action.insulation,
        insulationLoading: false,
      };
    case HeatTraceActionTypes.HeatTraceClearCircuits:
      return {
        ...state,
        circuits: null,
        circuitsLoading: false,
        currentTagNumber: null,
      };
    case HeatTraceActionTypes.HeatTraceClearInsulation:
      return {
        ...state,
        insulation: null,
        insulationLoading: false,
        currentInsulationTagNumber: null,
      };
    case HeatTraceActionTypes.HeatTraceLoadOverview:
      return {
        ...state,
        overview: null,
        overviewLoading: true,
        currentOverviewTagNumber: action.tagNumber,
      };
    case HeatTraceActionTypes.HeatTraceLoadOverviewSuccess:
      return {
        ...state,
        overview: normalizeOverview(action.overview),
        overviewLoading: false,
      };
    case HeatTraceActionTypes.HeatTraceClearOverview:
      return {
        ...state,
        overview: null,
        overviewLoading: false,
        currentOverviewTagNumber: null,
      };
    case HeatTraceActionTypes.HeatTraceClearAll:
      return { ...initialState };
    default:
      return state;
  }
}
